/**
 * Sync cursors for delta-resync on socket reconnect.
 *
 * After a reconnect we call `listConversations({ updatedAfter: since })`
 * instead of refetching the top 50 — same for per-conversation messages.
 * `since` lives here so handlers in different files can advance it as
 * socket events arrive.
 */

import { create } from 'zustand';
import { getMeta, setMeta } from '@/lib/db';

interface SyncState {
  lastSeenByKey: Record<string, string>;
  hydrated: boolean;

  setLastSeen: (key: string, iso: string) => void;
  getLastSeen: (key: string) => string | undefined;
  hydrate: () => Promise<void>;
  reset: () => void;
  // Drop messages:{convId} entries whose conversation isn't in
  // `liveConversationIds`. The conversation-list cursor and any entries
  // not matching the `messages:` prefix are preserved. Call this
  // periodically so the map doesn't grow unbounded over long sessions.
  pruneMessageCursors: (liveConversationIds: string[]) => void;
}

const META_KEY = 'lastSeenByKey';

function toIso(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

export const useSyncStore = create<SyncState>((set, get) => ({
  lastSeenByKey: {},
  hydrated: false,

  setLastSeen: (key, iso) => {
    const prev = get().lastSeenByKey[key];
    const next = toIso(prev, iso);
    if (next === prev) return;
    const nextMap = { ...get().lastSeenByKey, [key]: next! };
    set({ lastSeenByKey: nextMap });
    setMeta(META_KEY, nextMap).catch(() => {});
  },

  getLastSeen: (key) => get().lastSeenByKey[key],

  hydrate: async () => {
    if (get().hydrated) return;
    const stored = await getMeta<Record<string, string>>(META_KEY);
    set({ lastSeenByKey: stored ?? {}, hydrated: true });
  },

  reset: () => {
    set({ lastSeenByKey: {} });
    setMeta(META_KEY, {}).catch(() => {});
  },

  pruneMessageCursors: (liveConversationIds) => {
    const live = new Set(liveConversationIds);
    const current = get().lastSeenByKey;
    const next: Record<string, string> = {};
    let dropped = 0;
    for (const [key, value] of Object.entries(current)) {
      if (key.startsWith('messages:')) {
        const convId = key.slice('messages:'.length);
        if (!live.has(convId)) {
          dropped++;
          continue;
        }
      }
      next[key] = value;
    }
    if (dropped === 0) return;
    set({ lastSeenByKey: next });
    setMeta(META_KEY, next).catch(() => {});
  },
}));

export const SYNC_KEY_CONVERSATIONS = 'conversations:list';
export const syncKeyMessages = (conversationId: string) => `messages:${conversationId}`;
